import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import {
  createEditorStore,
  EditorStoreContext,
  useEditorStore,
} from "../../lib/stores/editor-store/editor-store";
import {
  CanvasStoreContext,
  createCanvasStore,
} from "@/lib/stores/canvas-store/canvas-store";
import EditorPanel from "./EditorPanel";
import EditorNavbar from "./Navbar/Navbar";
import { canEnlargeWindow, useIsMobile } from "../../lib/use-mobile";
import PreviewPanel from "./PreviewPanel";
import AddCreditsDialog from "../Billing/AddCreditsDialog";
import { useMissingCredits } from "../Billing/billing-hooks";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import { useTeamQuery } from "@/lib/queries/team-queries";
import CreativeProjectSheet from "./CreativeProject/CreativeProjectSheet";
import PartialAnimationDialog from "./ImagesTab/PartialAnimationDialog";
import { AnalyticsApi } from "@/lib/api/analytics-api";
import { SourceImageSelectorDialog } from "./SourceImageSelectorDialog";
import { TeamDto } from "@repo/api/team";

type EditorProps = {
  teamExternalId: string;
  videoExternalId: string;
};

function Editor({ teamExternalId, videoExternalId }: EditorProps) {
  const isInitialized = useEditorStore((state) => state.isInitialized);
  const { data: team } = useTeamQuery(teamExternalId);

  if (!isInitialized || !team) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-background-secondary">
        <LoadingSpinner size={32} />
      </div>
    );
  }

  return (
    <EditorLoaded
      teamExternalId={teamExternalId}
      videoExternalId={videoExternalId}
      team={team}
    />
  );
}

function EditorLoaded({
  teamExternalId,
  videoExternalId,
  team,
}: {
  teamExternalId: string;
  videoExternalId: string;
  team: TeamDto;
}) {
  const creditDialogState = useEditorStore((state) => state.creditDialogState);
  const { missingCredits, numSelected, numAnimatableImages } =
    useMissingCredits(teamExternalId);
  const [isPartialAnimationDialogOpen, setIsPartialAnimationDialogOpen] =
    useState(false);

  const setIsCreditDialogOpen = useEditorStore(
    (state) => state.service.setIsCreditDialogOpen,
  );
  const saveStateToCache = useEditorStore(
    (state) => state.service.saveStateToCache,
  );
  const isMobile = useIsMobile();

  const canAnimatePartially =
    !!numSelected && !!numAnimatableImages && numAnimatableImages < numSelected;

  const onCreditDialogOpenChange = useCallback(
    (isOpen: boolean) => {
      setIsCreditDialogOpen(isOpen);
      if (
        !isOpen &&
        creditDialogState.openedFromAnimate &&
        canAnimatePartially
      ) {
        setIsPartialAnimationDialogOpen(true);
        AnalyticsApi.logPartialAnimationPopupOpened(
          teamExternalId,
          numSelected,
          numAnimatableImages,
        );
      }
    },
    [
      setIsCreditDialogOpen,
      creditDialogState.openedFromAnimate,
      canAnimatePartially,
      teamExternalId,
      numSelected,
      numAnimatableImages,
    ],
  );

  return (
    <div className="flex h-full w-full flex-col gap-3 bg-background-secondary p-3 pb-0">
      <EditorNavbar />
      {!isMobile ? (
        <div className="flex flex-1 gap-3 overflow-hidden">
          <EditorPanel />
          <PreviewPanel />
          <CreativeProjectSheet />
          <SourceImageSelectorDialog />
        </div>
      ) : (
        <div className="flex w-full flex-1 items-center justify-center px-6 text-center">
          <p className="text-text-secondary">
            {canEnlargeWindow()
              ? "Make your browser window wider to edit videos."
              : "Switch to a computer to edit videos."}
          </p>
        </div>
      )}
      <AddCreditsDialog
        teamExternalId={teamExternalId}
        redirectVideoExternalId={videoExternalId}
        isOpen={creditDialogState.isOpen}
        setIsOpen={onCreditDialogOpenChange}
        onCreateCheckoutSuccess={saveStateToCache}
        missingCredits={missingCredits}
        numSelectedImages={numSelected}
        team={team}
      />
      {canAnimatePartially && (
        <PartialAnimationDialog
          isOpen={isPartialAnimationDialogOpen}
          onOpenChange={setIsPartialAnimationDialogOpen}
          numSelected={numSelected}
          numAnimatable={numAnimatableImages}
        />
      )}
    </div>
  );
}

function EditorProvider({
  teamExternalId,
  videoExternalId,
}: {
  teamExternalId: string;
  videoExternalId: string;
}) {
  const [editorStore] = useState(() => createEditorStore());
  const [canvasStore] = useState(() => createCanvasStore());

  useEffect(() => {
    const unsub = editorStore.subscribe((state) => {
      state.service.onStateChanged();
    });
    editorStore.getState().service.initialize(teamExternalId, videoExternalId);
    canvasStore.getState().service.initialize(editorStore);

    return () => {
      unsub();
      canvasStore.getState().service.destroy();
      editorStore.getState().service.destroy();
    };
  }, [videoExternalId, teamExternalId, editorStore, canvasStore]);

  return (
    <EditorStoreContext.Provider value={editorStore}>
      <CanvasStoreContext.Provider value={canvasStore}>
        <Editor
          teamExternalId={teamExternalId}
          videoExternalId={videoExternalId}
        />
      </CanvasStoreContext.Provider>
    </EditorStoreContext.Provider>
  );
}

export default function EditorPage() {
  const params = useParams();

  if (!params.videoExternalId || !params.teamExternalId) {
    return null;
  }

  return (
    <EditorProvider
      key={params.videoExternalId + params.teamExternalId}
      teamExternalId={params.teamExternalId}
      videoExternalId={params.videoExternalId}
    />
  );
}
